import { useCallback, useEffect, useState } from 'react';
import { supabase } from '../lib/supabase';
import { useVertical } from '../context/VerticalContext';

export interface SetupStep {
    id: string;
    label: string;
    hint: string;
    to: string;
    done: boolean;
}

/**
 * Progress through first-time setup, derived from what's actually in the
 * database rather than from tracking which buttons someone clicked.
 *
 * That distinction matters: a user who added their first customer before
 * seeing the checklist finds that step already ticked, and a step can't get
 * stuck "done" when the underlying record was later deleted. It also means the
 * list works as re-engagement for someone who set up half the app in week one
 * and came back in week three.
 */
export function useSetupProgress() {
    const { activeOrgId, molded } = useVertical();
    const [steps, setSteps] = useState<SetupStep[]>([]);
    const [loading, setLoading] = useState(true);

    // Kept separate from the state setters so every path through it awaits
    // before anything is committed — no synchronous setState from the effect.
    const buildSteps = useCallback(async (): Promise<SetupStep[]> => {
        if (!activeOrgId) return [];

        // A failed count must never tick a step it couldn't actually verify.
        const countOf = async (table: string) => {
            const { count, error } = await supabase
                .from(table)
                .select('id', { count: 'exact', head: true });
            return error ? 0 : (count ?? 0);
        };

        const countExpenses = async () => {
            const { count, error } = await supabase
                .from('ledger_headers')
                .select('id', { count: 'exact', head: true })
                .eq('transaction_type', 'expense')
                .is('deleted_at', null);
            return error ? 0 : (count ?? 0);
        };

        const [org, customers, jobs, invoices, suppliers, expenses] = await Promise.all([
            supabase
                .from('organizations')
                .select('business_address, business_phone, settings')
                .eq('id', activeOrgId)
                .single(),
            countOf('customers'),
            countOf('jobs'),
            countOf('invoices'),
            countOf('supplier_profiles'),
            countExpenses(),
        ]);

        const settings = (org.data?.settings ?? {}) as Record<string, unknown>;
        const { jargon } = molded;

        return [
            {
                id: 'business-details',
                label: 'Add your business details',
                hint: 'Address and phone — these print on every invoice you send.',
                to: '/settings?tab=shop',
                done: !!(org.data?.business_address || org.data?.business_phone),
            },
            {
                id: 'vat',
                label: 'Confirm your VAT position',
                hint: 'The single setting your whole dashboard is calculated from.',
                to: '/settings?tab=fiscal',
                done: settings.is_vat_registered !== undefined,
            },
            {
                id: 'labour-rate',
                label: 'Set your labour rate and parts markup',
                hint: 'Both pre-fill every quote and job, so you charge them consistently instead of by feel.',
                to: '/settings?tab=pricing',
                // Both, since the step now asks for both — a labour rate with
                // no markup still leaves parts priced at cost.
                done: Number(settings.default_labor_rate ?? 0) > 0
                    && Number(settings.default_parts_markup ?? 0) > 0,
            },
            {
                id: 'customer',
                label: `Add your first ${jargon.client_label.toLowerCase()}`,
                hint: `Add the ${jargon.entity_label.toLowerCase()} too — enter the ${jargon.asset_identifier} and tap Look up to pull the details from the DVLA.`,
                to: '/clients',
                done: customers > 0,
            },
            {
                id: 'job',
                label: `Complete your first ${jargon.action_label.toLowerCase()}`,
                hint: 'Book in, diagnose, quote, do the work — one real job end to end teaches the app faster than reading about it.',
                to: '/workshop',
                done: jobs > 0,
            },
            {
                id: 'invoice',
                label: 'Send your first invoice',
                hint: 'Card payments mark themselves paid; cash updates the moment you record it. Payment is what moves your tax, NI and VAT.',
                to: '/invoices',
                done: invoices > 0,
            },
            {
                id: 'supplier',
                label: 'Set up a supplier account',
                hint: 'Track what you buy on account and the credits owed back when parts go the other way.',
                to: '/returns',
                done: suppliers > 0,
            },
            {
                id: 'expense',
                label: 'Log your first expense',
                hint: `Go to ${jargon.sidebar.expenses}, tap Upload receipt, then take a photo or pick a file. Every cost recorded raises your Safe-to-Spend.`,
                to: '/expenses',
                done: expenses > 0,
            },
        ];
    }, [activeOrgId, molded]);

    const load = useCallback(async () => {
        const next = await buildSteps();
        setSteps(next);
        setLoading(false);
    }, [buildSteps]);

    useEffect(() => {
        let active = true;
        buildSteps().then((next) => {
            if (!active) return;
            setSteps(next);
            setLoading(false);
        });
        return () => { active = false; };
    }, [buildSteps]);

    const completed = steps.filter((s) => s.done).length;

    return {
        steps,
        loading,
        completed,
        total: steps.length,
        allDone: steps.length > 0 && completed === steps.length,
        refresh: load,
    };
}
